import type {
  AdminDepositReturnListItem,
  AdminDepositReturnListResponse,
} from '@inflip/contracts/admin';
import {
  DEPOSIT_RETURN_KIND_LABELS,
  DEPOSIT_RETURN_STATUS_LABELS,
  DepositReturnStatus,
  ORDER_STATUS_LABELS,
} from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { won } from '../components/confirm-deposit-dialog';
import { DataTable } from '../components/data-table';
import { DateRangeFilter } from '../components/date-range-filter';
import {
  CompleteDepositReturnDialog,
  EditDepositReturnAccountDialog,
} from '../components/deposit-returns-section';
import { Button, Chip, EmptyState, ErrorBanner, PageHeader } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { ORDER_STATUS_TEXT } from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';

const PAGE_SIZE = 20;

type DialogState =
  | null
  | { kind: 'complete'; item: AdminDepositReturnListItem }
  | { kind: 'account'; item: AdminDepositReturnListItem };

/**
 * 입금 반환 — 주문 대금이 아닌 입금(초과·부족·만료 후)을 돌려줄 건을 모아 본다.
 * 기본은 반환 대기 전체(기간 없음) — 송금할 건이 기간 밖으로 숨지 않게.
 * 송금한 뒤 [반환 완료], 계좌가 틀렸으면 [계좌 수정]. 취소는 없다 (받은 돈은 반드시 돌려준다)
 */
export function DepositReturnsPage() {
  const { session } = useAdminAuth();
  const [filter, setFilter] = useListViewState<DepositReturnStatus | ''>(
    'status',
    DepositReturnStatus.PENDING,
  );
  const [page, setPage] = useListViewState('page', 1);
  const [dateFrom, setDateFrom] = useListViewState('from', '');
  const [dateTo, setDateTo] = useListViewState('to', '');
  const [result, setResult] = useState<AdminDepositReturnListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .listDepositReturns(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        ...(filter ? { status: filter } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
      })
      .then(setResult)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session, filter, page, dateFrom, dateTo]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      await load();
      notifyPendingWorkChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // 그사이 다른 어드민이 처리했을 수 있다 — 지금 상태로 다시 그린다
      setDialog(null);
      await load();
    } finally {
      setPending(false);
    }
  }

  const columns = useMemo<ColumnDef<AdminDepositReturnListItem>[]>(
    () => [
      {
        id: 'status',
        header: '상태',
        size: 90,
        cell: ({ row }) => (
          <span
            className={`text-xs font-bold ${row.original.status === DepositReturnStatus.PENDING ? 'text-amber-600' : 'text-emerald-600'}`}
          >
            {DEPOSIT_RETURN_STATUS_LABELS[row.original.status]}
          </span>
        ),
      },
      {
        id: 'kind',
        header: '종류',
        size: 110,
        cell: ({ row }) => (
          <span className="text-sm font-semibold">
            {DEPOSIT_RETURN_KIND_LABELS[row.original.kind]}
          </span>
        ),
      },
      {
        id: 'order',
        header: '주문',
        cell: ({ row }) => {
          const r = row.original;
          return (
            <>
              <Link
                to="/orders/$orderId"
                params={{ orderId: r.orderId }}
                className="font-mono text-xs font-medium text-brand hover:underline"
              >
                {r.orderId.slice(0, 8)}
              </Link>
              <div
                className={`mt-0.5 text-[11px] font-semibold ${ORDER_STATUS_TEXT[r.orderStatus]}`}
              >
                {ORDER_STATUS_LABELS[r.orderStatus]}
              </div>
              {r.orderGroupId && (
                <Link
                  to="/orders"
                  search={{ q: r.orderGroupId, from: '', to: '' }}
                  className="text-[11px] text-muted-foreground hover:text-foreground hover:underline"
                >
                  묶음 <span className="font-mono">{r.orderGroupId.slice(0, 8)}</span>
                </Link>
              )}
            </>
          );
        },
      },
      {
        id: 'listing',
        header: '상품',
        cell: ({ row }) => (
          <span className="block truncate text-sm">{row.original.listingTitle ?? '(삭제됨)'}</span>
        ),
      },
      {
        id: 'buyer',
        header: '구매자',
        cell: ({ row }) => (
          <>
            <Link
              to="/users/$userId"
              params={{ userId: row.original.buyerId }}
              className="block truncate font-semibold text-brand hover:underline"
            >
              {row.original.buyerName ?? '—'}
            </Link>
            <div className="truncate text-xs text-muted-foreground">
              {row.original.buyerEmail ?? ''}
            </div>
          </>
        ),
      },
      {
        id: 'amount',
        header: '돌려줄 금액',
        meta: { align: 'right', tdClass: 'whitespace-nowrap tabular-nums' },
        cell: ({ row }) => (
          <>
            <div className="font-bold">{won(row.original.amount)}</div>
            {row.original.receivedAmount !== row.original.amount && (
              <div className="text-xs text-muted-foreground">
                입금 {won(row.original.receivedAmount)}
              </div>
            )}
          </>
        ),
      },
      {
        id: 'account',
        header: '계좌 · 사유',
        cell: ({ row }) => {
          const r = row.original;
          return (
            <>
              <div className="truncate text-sm">
                {r.bankAccount.bank} {r.bankAccount.accountNumber} ({r.bankAccount.holder})
              </div>
              <div className="truncate text-xs text-muted-foreground">
                입금자 {r.depositorName} · {r.reason}
              </div>
              {r.completedAt && (
                <div className="truncate text-xs text-emerald-700">
                  반환 {formatDateTime(r.completedAt)}
                  {r.completionNote ? ` · ${r.completionNote}` : ''}
                </div>
              )}
            </>
          );
        },
      },
      {
        id: 'createdAt',
        header: '생성일',
        cell: ({ row }) => formatDateTime(row.original.createdAt),
        meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
      },
      {
        id: 'actions',
        header: '처리',
        minSize: 170,
        meta: { align: 'right' },
        cell: ({ row }) =>
          row.original.status === DepositReturnStatus.PENDING ? (
            <div className="flex justify-end gap-1.5">
              <Button
                size="sm"
                variant="secondary"
                disabled={pending}
                onClick={() => setDialog({ kind: 'account', item: row.original })}
              >
                계좌 수정
              </Button>
              <Button
                size="sm"
                disabled={pending}
                onClick={() => setDialog({ kind: 'complete', item: row.original })}
              >
                반환 완료
              </Button>
            </div>
          ) : null,
      },
    ],
    [pending],
  );

  if (!session) return null;
  const token = session.accessToken;
  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="입금 반환"
        description={
          result
            ? `${result.total.toLocaleString('ko-KR')}건 · 초과·부족·만료 후 입금을 구매자에게 돌려줘요`
            : '초과·부족·만료 후 입금을 구매자에게 돌려줘요'
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <Chip
          active={filter === ''}
          onClick={() => {
            setPage(1);
            setFilter('');
          }}
        >
          전체
        </Chip>
        {Object.values(DepositReturnStatus).map((s) => (
          <Chip
            key={s}
            active={filter === s}
            onClick={() => {
              setPage(1);
              setFilter(s);
            }}
          >
            {DEPOSIT_RETURN_STATUS_LABELS[s]}
          </Chip>
        ))}
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={(from, to) => {
            setPage(1);
            setDateFrom(from);
            setDateTo(to);
          }}
        />
      </div>

      {error && <ErrorBanner message={error} />}

      {result && rows.length === 0 ? (
        <EmptyState message="해당 상태의 입금 반환이 없어요." />
      ) : (
        <DataTable data={rows} columns={columns} getRowId={(r) => r.id} />
      )}

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {result.page} / {result.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= result.totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}

      {dialog?.kind === 'complete' && (
        <CompleteDepositReturnDialog
          depositReturn={dialog.item}
          busy={pending}
          onSubmit={(note) =>
            void run(() => api.completeDepositReturn(token, dialog.item.id, { note }))
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'account' && (
        <EditDepositReturnAccountDialog
          depositReturn={dialog.item}
          busy={pending}
          onSubmit={(bankAccount, reason) =>
            void run(() =>
              api.updateDepositReturnAccount(token, dialog.item.id, { bankAccount, reason }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}
